import { Box, Chip, Divider, List, ListItem, Stack, styled } from "@mui/material";
import {
  DateRange,
  DateRangePickerToolbarProps,
  LocalizationProvider,
  pickerDayClasses,
  pickersLayoutClasses,
  PickersShortcutsItem,
  PickersShortcutsProps,
  useIsValidValue,
  usePickerActionsContext,
} from "@mui/x-date-pickers-pro";
import {
  DesktopDateRangePicker,
  DesktopDateRangePickerProps,
  DesktopDateRangePickerSlotProps,
} from "@mui/x-date-pickers-pro/DesktopDateRangePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { subDays, subMonths } from "date-fns";
import { ReactNode, useState } from "react";
import { getCurrentUtcDate } from "../../lib/Util";

type DateRangePickerProps = {
  start: Date | null;
  end: Date | null;
  defaultStart?: Date;
  defaultEnd?: Date;
  startLabel?: string;
  endLabel?: string;
  customHelper?: ReactNode;
  onChange: (date: DateRange<Date>) => void;
  icon?: ReactNode;
  showShortcuts?: boolean;
};

const currentUtcDate = getCurrentUtcDate();

const BoxStyled = styled(Box)(() => ({
  gridRow: "1 / 4 / auto ",
  gridColumn: "1 / 4",
  pt: 1,
}));

const ListStyled = styled(List)(({ theme }) => ({
  display: "flex",
  px: theme.spacing(1, 2),
  paddingBottom: theme.spacing(1),
  "& .MuiListItem-root": {
    padding: theme.spacing(0, 0.5),
  },
}));

const getShortcutsItems = (defaultStart?: Date, defaultEnd?: Date) => {
  const shortcutItems: PickersShortcutsItem<DateRange<Date>>[] = [
    {
      label: "Today",
      getValue: () => {
        return [currentUtcDate, currentUtcDate];
      },
    },
    {
      label: "Yesterday",
      getValue: () => {
        const yesterday = subDays(currentUtcDate, 1);
        return [yesterday, yesterday];
      },
    },
    {
      label: "7 Days",
      getValue: () => {
        return [subDays(currentUtcDate, 7), currentUtcDate];
      },
    },
    {
      label: "30 Days",
      getValue: () => {
        return [subDays(currentUtcDate, 30), currentUtcDate];
      },
    },
    {
      label: "3 Months",
      getValue: () => {
        return [subMonths(currentUtcDate, 3), currentUtcDate];
      },
    },
    {
      label: "6 Months",
      getValue: () => {
        return [subMonths(currentUtcDate, 6), currentUtcDate];
      },
    },
  ];

  if (defaultStart && defaultEnd) {
    shortcutItems.push({
      label: "Reset",
      getValue: () => {
        return [defaultStart, defaultEnd];
      },
    });
  }

  return shortcutItems;
};

const ChipStyled = styled(Chip)(() => ({
  fontSize: "10px",
}));

const CustomRangeShortcuts = (props: PickersShortcutsProps<DateRange<Date>>) => {
  const { items, changeImportance } = props;
  const { setValue } = usePickerActionsContext<DateRange<Date>>();

  const isValid = useIsValidValue();

  if (!items?.length) {
    return null;
  }
  const shorcutsList = items.map((item) => {
    const newValue = item.getValue({ isValid });

    return {
      label: item.label,
      onClick: () => {
        setValue(newValue, { changeImportance: changeImportance ?? "accept", shortcut: item });
      },
    };
  });

  return (
    <BoxStyled>
      <ListStyled dense>
        {shorcutsList.map((item) => {
          return (
            <ListItem key={item.label}>
              <ChipStyled {...item} />
            </ListItem>
          );
        })}
      </ListStyled>
      <Divider />
    </BoxStyled>
  );
};

const ToolbarContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  "&.MuiPickersLayout-toolbar": {
    gridArea: "3 / 1 / auto / 4",
  },
}));

const customToolbar = (customHelper: ReactNode, props: DateRangePickerToolbarProps) => {
  if (!customHelper) {
    return null;
  }

  return (
    <ToolbarContainer className={props.className} sx={{ fontSize: "13px" }}>
      <Stack direction={"row"} spacing={2} sx={{ alignItems: "center" }}>
        {customHelper}
      </Stack>
    </ToolbarContainer>
  );
};

const isDateInFuture = (date: Date | null) => {
  return date && currentUtcDate < date;
};

const isRangeEndGreaterThanRangeStart = (value: DateRange<Date>) => {
  return value[0] && value[1] && value[0] > value[1];
};

interface DateRangePickerButtonProps {
  "data-testid": string;
}
interface PickerSlotProps extends DesktopDateRangePickerSlotProps {
  textfield?: DateRangePickerButtonProps;
}

const DateRangePicker = ({
  start,
  end,
  defaultStart,
  defaultEnd,
  startLabel = "",
  endLabel = "",
  customHelper,
  onChange,
  disableFuture,
  showShortcuts = true,
  ...props
}: DateRangePickerProps & DesktopDateRangePickerProps) => {
  const [draftValue, setDraftValue] = useState<DateRange<Date>>([start, end]);
  const [prevStart, setPrevStart] = useState(start);
  const [prevEnd, setPrevEnd] = useState(end);
  if (prevStart !== start || prevEnd !== end) {
    setPrevStart(start);
    setPrevEnd(end);
    setDraftValue([start, end]);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} localeText={{ start: startLabel, end: endLabel }}>
      <DesktopDateRangePicker
        sx={{
          height: "35px",
          "& p.MuiTypography-root": {
            ml: 1,
          },
          "& .MuiTextField-root:last-child": {
            ml: 1,
          },
          "& .MuiOutlinedInput-root, & .MuiTextField-root": {
            height: "100%",
          },
          ...props.sx,
        }}
        value={draftValue}
        onChange={(value: DateRange<Date>) => {
          // MUI fires onChange([newStart, oldEnd]) when clicking a new start on a complete range; null the end to keep the picker open.
          const startChanged = value[0]?.getTime() !== draftValue[0]?.getTime();
          const endUnchanged = value[1]?.getTime() === draftValue[1]?.getTime();
          if (startChanged && endUnchanged && draftValue[1]) {
            setDraftValue([value[0], null]);
            return;
          }

          setDraftValue(value);
          if (!value[0] || !value[1]) {
            return;
          }

          if (disableFuture) {
            if (isDateInFuture(value[0])) {
              value[0] = currentUtcDate;
            }

            if (isDateInFuture(value[1])) {
              value[1] = currentUtcDate;
            }
          }

          if (isRangeEndGreaterThanRangeStart(value)) {
            value[1] = value[0];
          }

          onChange(value);
        }}
        slots={{
          toolbar: (props: DateRangePickerToolbarProps) => customToolbar(customHelper, props),
          shortcuts: showShortcuts ? CustomRangeShortcuts : undefined,
        }}
        slotProps={
          {
            layout: {
              sx: {
                "& .MuiPickersCalendarHeader-label": {
                  fontSize: "18px",
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  fontSize: "12px",
                },
                [`&.${pickersLayoutClasses.root}`]: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                },
              },
            },
            day: {
              sx: {
                "& .MuiButtonBase-root": {
                  fontSize: "12px",
                },
                [`& .${pickerDayClasses.selected}`]: {
                  color: "text10.main",
                },
              },
            },
            shortcuts: {
              items: showShortcuts ? getShortcutsItems(defaultStart, defaultEnd) : [],
            },
            textField: {
              "data-testid": "date-range-picker",
            },
          } as PickerSlotProps
        }
        maxDate={currentUtcDate}
        {...props}
      />
    </LocalizationProvider>
  );
};

export default DateRangePicker;
